
import React, { useState } from 'react';
import { Resource } from '@/contexts/ResourcesContext';
import { Button } from '@/components/ui/button';
import { Image as ImageIcon, ImageOff } from 'lucide-react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import ImageEditor from './ImageEditor';
import { getDefaultImageForType } from '@/utils/resourceUtils';

interface ResourceCardImageProps {
  resource: Resource;
  isAdmin: boolean;
  onImageUpdate: (imageUrl: string) => void;
}

const ResourceCardImage: React.FC<ResourceCardImageProps> = ({ 
  resource, 
  isAdmin, 
  onImageUpdate 
}) => {
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const handleImageError = () => {
    console.log("Resource image failed to load:", resource.imageUrl);
    setImageError(true);
  };

  const handleImageUploadClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation when clicking the button
    if (isAdmin) {
      setIsImageDialogOpen(true);
    }
  };

  const handleSaveImage = (imageUrl: string) => {
    onImageUpdate(imageUrl);
    setImageError(false);
  };

  // Get image URL with fallback
  const getImageUrl = () => {
    if (imageError || !resource.imageUrl) {
      return getDefaultImageForType(resource.type);
    }
    return resource.imageUrl;
  };
  
  return (
    <div className="relative">
      <AspectRatio ratio={16/9}>
        {imageError ? (
          <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center text-gray-500">
            <ImageOff className="h-8 w-8 mb-2" />
            <span>Image not available</span>
          </div>
        ) : (
          <img 
            src={getImageUrl()} 
            alt={resource.title} 
            className="object-cover w-full h-full" 
            onError={handleImageError}
          />
        )}
      </AspectRatio>
      
      {isAdmin && (
        <Button
          variant="secondary"
          size="sm"
          className="absolute bottom-2 right-2 bg-white/80 hover:bg-white"
          onClick={handleImageUploadClick}
        >
          <ImageIcon className="h-4 w-4 mr-1" /> Edit
        </Button>
      )}

      {/* Image Upload Dialog */}
      <ImageEditor 
        isOpen={isImageDialogOpen}
        onClose={() => setIsImageDialogOpen(false)}
        onSave={handleSaveImage}
        originalImageUrl={resource.imageUrl || ''}
        defaultImageUrl={getDefaultImageForType(resource.type)}
        title="Change Resource Image"
      />
    </div>
  );
};

export default ResourceCardImage;
